'use client';

/**
 * NeuroplasticSection - Unified dynamic section component
 * 
 * This is the main component that renders ANY section type dynamically
 * based on JSON configuration. No hardcoded section components needed.
 * 
 * Supports layouts:
 * - hero: Full-screen with background
 * - split: Two-column (content + media)
 * - split-reverse: Two-column (media + content)
 * - grid: Card grid
 * - timeline: Timeline/story layout
 * - carousel: Horizontal scrolling
 * - full-width: Full-width content
 * - stacked: Vertically stacked
 * - centered: Centered content
 */

import React, { useMemo } from 'react';
import { NeuroplasticSectionProps, NeuroplasticSectionConfig } from './types';
import { BackgroundLayer } from './elements/BackgroundLayer';
import { ContentBlock } from './elements/ContentBlock';
import { MediaBlock } from './elements/MediaBlock';
import { CTAButtons } from './elements/CTAButtons';
import { CardGrid } from './elements/CardGrid';
import { FeatureList } from './elements/FeatureList';
import { SectionProvider } from '@/contexts/SectionContext';
import { getBrandColor } from '@/config/brand-colors';

// ============================================================================
// Layout Components
// ============================================================================

function HeroLayout({ config }: { config: NeuroplasticSectionConfig }) {
  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ minHeight: config.minHeight || '100vh' }}
    >
      <BackgroundLayer config={config.background} priority />
      
      <div className="container mx-auto px-4 relative z-20">
        <div className="max-w-4xl mx-auto text-center">
          <ContentBlock 
            config={config.content} 
            animation={{ delay: 0.2, stagger: 0.15 }}
          />
          <CTAButtons 
            config={config.cta} 
            animation={{ delay: 0.6 }}
            className="mt-8"
          />
        </div>
      </div>
    </section>
  );
}

function SplitLayout({ config, reverse = false }: { config: NeuroplasticSectionConfig; reverse?: boolean }) {
  const contentOrder = reverse ? 'lg:order-2' : 'lg:order-1';
  const mediaOrder = reverse ? 'lg:order-1' : 'lg:order-2';

  return (
    <section
      className="relative py-16 md:py-24 overflow-hidden"
      style={{ backgroundColor: config.background?.type === 'color' ? config.background.value : getBrandColor('white') }}
    >
      <BackgroundLayer config={config.background?.type !== 'color' ? config.background : undefined} />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Content */}
            <div className={contentOrder}>
              <ContentBlock 
                config={config.content} 
                animation={{ delay: 0.1, stagger: 0.1 }}
              />
              {config.features && (
                <FeatureList 
                  config={config.features} 
                  animation={{ delay: 0.4 }}
                  className="mt-8"
                />
              )}
              <CTAButtons 
                config={config.cta} 
                animation={{ delay: 0.5 }}
                className="mt-8"
              />
            </div>
            
            {/* Media */}
            <div className={mediaOrder}>
              <MediaBlock 
                config={config.media} 
                animation={{ delay: 0.2 }}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function GridLayout({ config }: { config: NeuroplasticSectionConfig }) {
  return (
    <section
      className="relative py-16 md:py-24 overflow-hidden"
      style={{ backgroundColor: config.background?.type === 'color' ? config.background.value : getBrandColor('white') }}
    >
      <BackgroundLayer config={config.background?.type !== 'color' ? config.background : undefined} />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          {config.content && (
            <ContentBlock 
              config={{ ...config.content, alignment: 'center' }} 
              animation={{ delay: 0.1 }}
              className="mb-12"
            />
          )}
          
          {/* Cards Grid */}
          <CardGrid 
            config={config.cards} 
            animation={{ delay: 0.3, stagger: 0.1 }}
          />
          
          {/* CTA */}
          {config.cta && (
            <CTAButtons 
              config={{ ...config.cta, alignment: 'center' }} 
              animation={{ delay: 0.6 }}
              className="mt-12"
            />
          )}
        </div>
      </div>
    </section>
  );
}

function StackedLayout({ config }: { config: NeuroplasticSectionConfig }) {
  return (
    <section
      className="relative py-16 md:py-24 overflow-hidden"
      style={{ backgroundColor: config.background?.type === 'color' ? config.background.value : getBrandColor('white') }}
    >
      <BackgroundLayer config={config.background?.type !== 'color' ? config.background : undefined} />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Content */}
          <ContentBlock 
            config={config.content} 
            animation={{ delay: 0.1 }}
          />
          
          {/* Media */}
          {config.media && (
            <MediaBlock 
              config={config.media} 
              animation={{ delay: 0.3 }}
              className="mt-8"
            />
          )}
          
          {/* Features */}
          {config.features && (
            <FeatureList 
              config={config.features} 
              animation={{ delay: 0.4 }}
              className="mt-8"
            />
          )}
          
          {/* Cards */}
          {config.cards && (
            <CardGrid 
              config={config.cards} 
              animation={{ delay: 0.5 }}
              className="mt-8"
            />
          )}
          
          {/* CTA */}
          <CTAButtons 
            config={config.cta} 
            animation={{ delay: 0.6 }}
            className="mt-8"
          />
        </div>
      </div>
    </section>
  );
}

function CenteredLayout({ config }: { config: NeuroplasticSectionConfig }) {
  return (
    <section
      className="relative py-16 md:py-24 overflow-hidden"
      style={{ backgroundColor: config.background?.type === 'color' ? config.background.value : getBrandColor('white') }}
    >
      <BackgroundLayer config={config.background?.type !== 'color' ? config.background : undefined} />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <ContentBlock 
            config={{ ...config.content, alignment: 'center' }} 
            animation={{ delay: 0.1 }}
          />
          
          {config.media && (
            <MediaBlock 
              config={config.media} 
              animation={{ delay: 0.3 }}
              className="mt-8 mx-auto max-w-2xl"
            />
          )}
          
          <CTAButtons 
            config={{ ...config.cta, alignment: 'center' }} 
            animation={{ delay: 0.5 }}
            className="mt-8"
          />
        </div>
      </div>
    </section>
  );
}

function FullWidthLayout({ config }: { config: NeuroplasticSectionConfig }) {
  return (
    <section
      className="relative py-16 md:py-24 overflow-hidden"
      style={{ backgroundColor: config.background?.type === 'color' ? config.background.value : getBrandColor('white') }}
    >
      <BackgroundLayer config={config.background?.type !== 'color' ? config.background : undefined} />
      
      <div className="relative z-10">
        {/* Full-width header */}
        {config.content && (
          <div className="container mx-auto px-4 mb-12">
            <ContentBlock 
              config={config.content} 
              animation={{ delay: 0.1 }}
            />
          </div>
        )}
        
        {/* Full-width media */}
        {config.media && (
          <MediaBlock 
            config={{ ...config.media, rounded: false }}
            animation={{ delay: 0.3 }}
            className="w-full"
          />
        )}
        
        {/* Full-width cards carousel */}
        {config.cards && (
          <div className="overflow-x-auto py-4">
            <div className="flex gap-6 px-4">
              <CardGrid 
                config={config.cards} 
                animation={{ delay: 0.4 }}
              />
            </div>
          </div>
        )}
        
        {/* CTA */}
        {config.cta && (
          <div className="container mx-auto px-4 mt-12">
            <CTAButtons 
              config={config.cta} 
              animation={{ delay: 0.6 }}
            />
          </div>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function NeuroplasticSection({ 
  config, 
  isFirstSection = false, 
  sectionIndex = 0,
  debug = false 
}: NeuroplasticSectionProps) {
  // Select layout component based on config
  const LayoutComponent = useMemo(() => {
    switch (config.layout) {
      case 'hero':
        return HeroLayout;
      case 'split':
        return (props: { config: NeuroplasticSectionConfig }) => <SplitLayout {...props} reverse={false} />;
      case 'split-reverse':
        return (props: { config: NeuroplasticSectionConfig }) => <SplitLayout {...props} reverse={true} />;
      case 'grid':
        return GridLayout;
      case 'stacked':
        return StackedLayout;
      case 'centered':
        return CenteredLayout;
      case 'full-width':
        return FullWidthLayout;
      default:
        return StackedLayout;
    }
  }, [config.layout]);

  // Debug mode
  if (debug) {
    console.log('[NeuroplasticSection] Rendering:', {
      id: config.id,
      sectionKey: config.sectionKey,
      layout: config.layout,
      type: config.type,
    });
  }

  return (
    <SectionProvider sectionId={config.id}>
      <div
        data-section={config.sectionKey}
        data-section-id={config.id}
        data-section-type={config.type}
        data-layout={config.layout}
        className={config.className}
        style={config.style as React.CSSProperties}
      >
        <LayoutComponent config={config} />
      </div>
    </SectionProvider>
  );
}

export default NeuroplasticSection;
